import { type ReactNode, useState } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '~shared/shadcn/ui/card';

interface CardAction {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  variant?:
    | 'default'
    | 'outline'
    | 'secondary'
    | 'destructive'
    | 'ghost'
    | 'link';
}

interface StatusBadge {
  label: string;
  className?: string;
}

interface PaymentProviderCardProps {
  name: string;
  logo: string;
  isActive: boolean;
  isComingSoon: boolean;
  primaryAction?: CardAction;
  secondaryAction?: CardAction;
  tertiaryAction?: CardAction;
  statusBadges?: StatusBadge[];
  /** @deprecated Use primaryAction instead */
  onAction?: () => void;
}

export default function PaymentProviderCard({
  name,
  logo,
  isActive,
  isComingSoon,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  statusBadges,
  onAction,
}: PaymentProviderCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const resolvedPrimaryAction =
    primaryAction ??
    (onAction
      ? {
          label: isActive ? 'Manage' : 'Connect',
          onClick: onAction,
          variant: isActive ? 'outline' : 'default',
        }
      : undefined);

  return (
    <Card
      className={`overflow-hidden rounded-2xl border-0 transition-all duration-300 ${
        isHovered ? 'scale-[1.02] shadow-lg' : 'shadow-md'
      } bg-background/60 backdrop-blur-sm`}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <CardHeader className='flex flex-row items-center justify-between px-6 pb-2 pt-6'>
        <div
          className={cn(
            'flex size-16 items-center justify-center',
            name === 'Authorize.net' && 'w-28',
          )}
        >
          <img
            src={logo || '/placeholder.svg'}
            alt={`${name} logo`}
            className='object-contain'
          />
        </div>
        {statusBadges && statusBadges.length > 0 ? (
          <div className='flex flex-col items-end gap-2'>
            {statusBadges.map((badge) => (
              <Badge
                key={badge.label}
                variant='default'
                className={cn(
                  'rounded-full px-3',
                  badge.className ??
                    'bg-green-500 hover:bg-green-600',
                )}
              >
                {badge.label}
              </Badge>
            ))}
          </div>
        ) : (
          isActive && (
            <Badge
              variant='default'
              className='rounded-full bg-green-500 px-3 hover:bg-green-600'
            >
              Active
            </Badge>
          )
        )}
        {isComingSoon && (
          <Badge variant='secondary' className='rounded-full px-3'>
            Coming Soon
          </Badge>
        )}
      </CardHeader>
      <CardContent className='px-6'>
        <h3 className='mb-2 text-xl font-semibold'>{name}</h3>
      </CardContent>
      <CardFooter className='flex flex-col gap-2 px-6 pb-6'>
        {isComingSoon ? (
          <Button disabled className='w-full rounded-xl' variant='outline'>
            Coming Soon
          </Button>
        ) : (
          <>
            {resolvedPrimaryAction && (
              <Button
                onClick={resolvedPrimaryAction.onClick}
                className={cn('w-full rounded-xl', resolvedPrimaryAction.className)}
                variant={resolvedPrimaryAction.variant ?? 'default'}
                disabled={resolvedPrimaryAction.disabled}
              >
                {resolvedPrimaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                onClick={secondaryAction.onClick}
                className={cn('w-full rounded-xl', secondaryAction.className)}
                variant={secondaryAction.variant ?? 'outline'}
                disabled={secondaryAction.disabled}
                aria-label={secondaryAction.ariaLabel}
              >
                {secondaryAction.label}
              </Button>
            )}
            {tertiaryAction && (
              <Button
                onClick={tertiaryAction.onClick}
                className={cn('w-full rounded-xl', tertiaryAction.className)}
                variant={tertiaryAction.variant ?? 'ghost'}
                disabled={tertiaryAction.disabled}
                aria-label={tertiaryAction.ariaLabel}
              >
                {tertiaryAction.label}
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
}
